import React, {
  ComponentType,
  useState
} from 'react'
import { connect } from 'react-redux'
import {
  Button,
  Container,
  Form,
  Grid,
  Icon,
  Message
} from 'semantic-ui-react'
import { Dispatch } from 'redux'
// Local
import { switchUserRole } from '../../action/user'
import { formatRoleName } from '../common/formatUtils'
import { withPrimaryRoles } from '../common/roleUtils'
import { User } from '../authenticated/types'
import {
  ROLES,
  shouldRenderReskin2025
} from '../../constant'
import If from '../common/if'
import ReveleerButton from '../common/ReveleerButton'
import { RootState } from '../../store'
import ReveleerCoachingBox from '../ReveleerCoachingBox'

const SafeGrid = Grid as unknown as ComponentType<any>
const SafeForm = Form as unknown as ComponentType<any>

/**
 * Provides a component for changing the current user's active role. When the
 * active role is changed, a user redux-action is triggered that ultimately
 * updates the `type` property on the user in the store and redirects back to
 * home
 * @param currentUser Current user entity as defined in redux state.user
 */

const mapStateToProps = (state: RootState) => ({
  currentUser: state.user
})

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  switchUserRoleProp: (
    currentUser: User,
    role: keyof typeof ROLES
  ) => dispatch(switchUserRole(
    currentUser,
    role
  ))
})

type ReduxStateProps = ReturnType<typeof mapStateToProps>
type ReduxDispatchProps = ReturnType<typeof mapDispatchToProps>

type Props = ReduxStateProps & ReduxDispatchProps

function RoleSelect(
  props: Props
): JSX.Element {
  const {
    currentUser,
    switchUserRoleProp
  } = props
  const [
    roleUpdateState,
    setRoleUpdateState
  ] = useState<keyof typeof ROLES>(currentUser.type)

  const onChangeRole = (
    e: any,
    { value }: { value: keyof typeof ROLES }
  ): void => {
    setRoleUpdateState(value)
  }

  const handleChangeRoleClick = (): void => {
    if (currentUser.type !== roleUpdateState) {
      switchUserRoleProp(
        currentUser,
        roleUpdateState
      )
    }
  }

  const roles = withPrimaryRoles(currentUser.roles)

  return (
    <Container>
      <SafeGrid>
        <Grid.Row>
          <Grid.Column
            mobile={ 16 }
            tablet={ 16 }
            computer={ 16 }
          >
            <If isTrue={ !shouldRenderReskin2025 }>
              <Message>
                <Icon name="info circle" />
                Your user account has access to multiple roles. Change your role
                by selecting an option below and clicking &apos;Change Role&apos;. You
                will be redirected to home.
              </Message>
            </If>
            <If isTrue={ shouldRenderReskin2025 }>
              <ReveleerCoachingBox
                coachingBoxType="infoNegative"
                isShowing
                message={
                  <p>
                    Your user account has access to multiple roles. Change your role
                    by selecting an option below and clicking &apos;Change Role&apos;. You
                    will be redirected to home.
                  </p>
                }
              />
            </If>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <SafeForm>
            <Form.Field label="Available Roles" />
            {
              roles.map(role => (
                <Form.Radio
                  key={ role.id }
                  label={ formatRoleName(role.name) }
                  name={ role.name }
                  value={ role.name }
                  checked={ roleUpdateState === role.name }
                  onChange={ onChangeRole }
                />
              ))
            }
          </SafeForm>
        </Grid.Row>
        <If isTrue={ roleUpdateState !== currentUser.type }>
          <Grid.Row>
            <Grid.Column
              mobile={ 16 }
              tablet={ 16 }
              computer={ 16 }
              textAlign="right"
            >
              <If isTrue={ !shouldRenderReskin2025 }>
                <Button
                  content="Change Role"
                  id="change-role-button"
                  onClick={ handleChangeRoleClick }
                />
              </If>
              <If isTrue={ shouldRenderReskin2025 }>
                <ReveleerButton
                  buttonLabel="Change Role"
                  buttonSize="small"
                  buttonType="secondary"
                  id="change-role-button"
                  isDisabled={ false }
                  onClick={ handleChangeRoleClick }
                  type="button"
                />
              </If>
            </Grid.Column>
          </Grid.Row>
        </If>
      </SafeGrid>
    </Container>
  )
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(RoleSelect)
